import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  createWorkMaterialConsumption,
  deleteWorkMaterialConsumption,
  getWorkMaterialConsumptions,
  type WorkMaterialConsumption,
} from "../../../marcos/workMaterialConsumptionsApi";
import { formatCurrency, formatDateShort } from "../obraFormat";

interface WorkMaterialsTabProps {
  workId: number;
}

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Parse a localized/plain number string into a finite number, or null when blank/invalid.
const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatQuantity = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

export function WorkMaterialsTab({ workId }: WorkMaterialsTabProps) {
  const [items, setItems] = useState<WorkMaterialConsumption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [descricao, setDescricao] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [unidade, setUnidade] = useState("");
  const [custoUnitario, setCustoUnitario] = useState("");
  const [data, setData] = useState(todayInput());
  const [notas, setNotas] = useState("");

  const loadItems = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        setItems(await getWorkMaterialConsumptions(workId));
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os materiais.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setDescricao("");
    setQuantidade("");
    setUnidade("");
    setCustoUnitario("");
    setData(todayInput());
    setNotas("");
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (!descricao.trim()) {
      setError("A descrição é obrigatória.");
      return;
    }
    const quantity = parseNumber(quantidade);
    if (quantity === null || quantity <= 0) {
      setError("Indique uma quantidade válida (> 0).");
      return;
    }
    if (!data) {
      setError("A data é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createWorkMaterialConsumption({
        workId,
        description: descricao.trim(),
        quantity,
        unit: unidade.trim() !== "" ? unidade.trim() : null,
        unitCost: parseNumber(custoUnitario),
        consumedOn: data,
        notes: notas.trim() !== "" ? notas.trim() : null,
      });
      setItems((prev) => [created, ...prev]);
      resetForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível adicionar o material.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteWorkMaterialConsumption(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems((prev) => prev.filter((item) => item.id !== id));
        setConfirmDeleteId(null);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar o material.",
        );
        loadItems(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const totalMateriais = items.reduce(
    (sum, item) => sum + (Number.isFinite(item.totalCost) ? item.totalCost : 0),
    0,
  );

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border p-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Descrição *
            <input
              type="text"
              maxLength={300}
              value={descricao}
              onChange={(event) => setDescricao(event.target.value)}
              placeholder="Material consumido"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Quantidade *
            <input
              type="number"
              min={0}
              step={0.001}
              value={quantidade}
              onChange={(event) => setQuantidade(event.target.value)}
              placeholder="0"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Unidade
            <input
              type="text"
              maxLength={20}
              value={unidade}
              onChange={(event) => setUnidade(event.target.value)}
              placeholder="un, m², kg…"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Custo unitário €
            <input
              type="number"
              min={0}
              step={0.01}
              value={custoUnitario}
              onChange={(event) => setCustoUnitario(event.target.value)}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Data *
            <input
              type="date"
              value={data}
              onChange={(event) => setData(event.target.value)}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Notas
            <textarea
              value={notas}
              maxLength={1000}
              rows={2}
              onChange={(event) => setNotas(event.target.value)}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={handleAdd}
            disabled={isSaving}
            className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isSaving ? "A adicionar…" : "Adicionar"}
          </button>
        </div>
      </div>

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: "760px" }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3 text-right" style={{ width: "140px" }}>Quantidade</th>
              <th className="px-4 py-3 text-right" style={{ width: "130px" }}>Custo unit.</th>
              <th className="px-4 py-3 text-right" style={{ width: "140px" }}>Custo total</th>
              <th className="px-4 py-3" style={{ width: "120px" }}>Data</th>
              <th className="px-4 py-3" style={{ width: "160px" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-secondary-text">
                  Sem materiais registados. Use o formulário acima para adicionar o
                  primeiro.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 break-words">
                    {item.description || "—"}
                    {item.notes ? (
                      <span className="block text-secondary-text text-xs">
                        {item.notes}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatQuantity(item.quantity)}
                    {item.unit ? ` ${item.unit}` : ""}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(item.unitCost)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                    {formatCurrency(item.totalCost)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDateShort(item.consumedOn)}
                  </td>
                  <td className="px-4 py-3">
                    {confirmDeleteId === item.id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-secondary-text">Apagar?</span>
                        <button
                          type="button"
                          disabled={deletingId === item.id}
                          onClick={() => handleDelete(item.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {deletingId === item.id ? "A eliminar…" : "Confirmar"}
                        </button>
                        <button
                          type="button"
                          disabled={deletingId === item.id}
                          onClick={() => setConfirmDeleteId(null)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(item.id)}
                        className="text-destructive cursor-pointer"
                      >
                        Apagar
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <span className="text-secondary-text text-sm">
          Total de materiais:{" "}
          <span className="text-foreground font-medium">
            {formatCurrency(totalMateriais)}
          </span>
        </span>
      </div>
    </div>
  );
}
